/**
 * Shared App Proxy identity for ALL backend calls (order lists + draft orders).
 *
 * The backend (`ceres-b2b-integration`) verifies every storefront endpoint via
 * @shopify_app_frontend_auth against the ceres-b2b-app client secret and
 * derives the company server-side from the signed customer id — callers never
 * send a company id. The two-step dance it requires:
 *
 *  1. POST customer-auth through the App Proxy (so Shopify HMAC-signs the
 *     request) to mint a token — the backend echoes the signed query string
 *     back base64-encoded as `{ signature }`.
 *  2. Send that token as `Authorization: Bearer <token>` on backend calls.
 *
 * The token is valid for the backend's 30-minute signature window; cached for
 * slightly less so a fresh one is always minted in time.
 */
import API_ENDPOINTS from '../config/api.js';

const TOKEN_TTL_MS = 25 * 60 * 1000;

let cachedToken = null;
let cachedTokenExpiresAt = 0;
let tokenPromise = null;

function mintAuthToken() {
  return fetch(API_ENDPOINTS.CUSTOMER_AUTH_PROXY, { method: 'POST' })
    .then((res) => {
      if (!res.ok) throw new Error(`customer-auth HTTP ${res.status}`);
      return res.json();
    })
    .then(({ signature }) => {
      if (!signature) throw new Error('customer-auth: missing signature in response');
      cachedToken = signature;
      cachedTokenExpiresAt = Date.now() + TOKEN_TTL_MS;
      return cachedToken;
    });
}

/** Get a valid Authorization token, reusing the cached one while it's fresh. */
export function getAuthToken() {
  if (cachedToken && Date.now() < cachedTokenExpiresAt) {
    return Promise.resolve(cachedToken);
  }
  if (!tokenPromise) {
    tokenPromise = mintAuthToken().finally(() => {
      tokenPromise = null;
    });
  }
  return tokenPromise;
}

/** fetch() with the App Proxy bearer token added to the headers. */
export async function proxyFetch(url, options = {}) {
  const token = await getAuthToken();
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
}
